import { CommonModule } from '@angular/common';
import { Component, OnInit, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';

import { environment } from 'src/environments/environment.dev';

import { BaseComponent } from 'src/app/@core/components/base/base.component';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { DatepickerComponent } from 'src/app/@core/components/ui/datepicker/datepicker.component';
import { DeleteConfirmationComponent } from 'src/app/@core/components/ui/delete-confirmation/delete-confirmation.component';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { USER_ROLE } from 'src/app/@core/models/enums';
import { UI_FEATURE } from 'src/app/@core/models/feature-mapping';
import { IUser, SHARE_ACCESS } from 'src/app/@core/models/model';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { FeatureFlagService } from 'src/app/@core/services/feature-flag.service';
import { SubscriptionStateService } from 'src/app/@core/services/subscription-state.service';

import { AiAssistantComponent } from 'src/app/@shared/components/ai-assistant/ai-assistant.component';
import { LottieAnimationComponent } from 'src/app/@shared/components/lottie-animation/lottie-animation.component';
import { GenerateDocumentComponent } from 'src/app/@shared/components/generate-document/generate-document.component';
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';

import { OrganizationSetupFormService } from 'src/app/contract-management/services/organization-setup-from.service';
import { ContractService, DOCUMENT_CREATION_SOURCE } from 'src/app/contract-management/services/contracts.service';
import { DocumentTypeService, DocumentTypeFilter } from 'src/app/contract-management/services/document-type.service';
import { IDocumentType } from 'src/app/contract-management/models/settings.model';

import { EXPLORE_PAGE_SVG } from 'src/app/common/svgs';

import { WorkspaceOverviewComponent } from '../../components/workspace-overview/workspace-overview.component';

/**
 * Explore landing page.
 *
 * Renders the feature-launch cards (Generate with AI, Contract Generator,
 * Geotechnical Analysis, Chat with Bob) plus the new Workspace Overview
 * dashboard. The legacy Recent Projects / Recent Documents blocks were
 * removed in favour of the dashboard widgets.
 *
 * The rename / due-date / delete dialogs are kept in the template so they
 * remain available if workspace-overview widgets need to wire actions to
 * them later.
 */
@Component({
    selector: 'app-explore',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TooltipModule,
        InputTextModule,
        LottieAnimationComponent,
        AiAssistantComponent,
        GenerateDocumentComponent,
        AssetUrlPipe,
        ButtonComponent,
        IconComponent,
        DialogComponent,
        DatepickerComponent,
        DeleteConfirmationComponent,
        WorkspaceOverviewComponent,
    ],
    templateUrl: './explore.component.html',
    styleUrl: './explore.component.scss'
})
export class ExploreComponent extends BaseComponent implements OnInit {
    /** Rename document dialog state. */
    rename: { open: boolean; newName: string; contractId: string } = { open: false, newName: '', contractId: '' };

    /** Due-date dialog state. */
    dueDate: { open: boolean; selectedDate: Date | null; contractId: string } = { open: false, selectedDate: null, contractId: '' };

    /** Delete-confirmation dialog state. */
    deleteDialogVisible = false;
    contractToDelete: { id: string; name: string } | null = null;
    currentDate: Date = new Date();

    /** Inline SVG assets used by the feature cards. */
    generateWithAISvg: SafeHtml;
    rfpGeneratorSvg: SafeHtml;
    contractGeneratorSvg: SafeHtml;
    chatWithBobSvg: SafeHtml;
    proposalGeneratorSvg: SafeHtml;
    geotechnicalAnalysisSvg: SafeHtml;

    currentUser!: IUser;
    subscriptionDetails: any;

    /** True while a Stripe payment-return is being processed. */
    isProcessingPayment = false;

    /** Welcome animation properties. */
    showWelcomeAnimation = false;
    welcomeAnimationData = null;

    /** Celebration animation properties (post-payment). */
    showCelebrationAnimation = false;
    celebrationAnimationData = null;

    /** AI assistant dialog state. */
    aiAssistant = { open: false };

    /** "Generate contract" dialog state. */
    generateContract = { open: false };

    docTypes: IDocumentType[] = [];

    /**
     * Activation flags for feature cards in the template. `true` shows the
     * card normally; `false` shows the "Coming soon" overlay.
     */
    cardStatus: Record<string, boolean> = {
        innDocAgent: true,
        createDocument: true,
        generateWithAI: true,

        generateContract: false,
        redlining: false,
        complianceCheck: false,
        compareContracts: false,

        generateProposal: false,
        rfpVsProposal: false,
        compareProposals: false,

        specAnalyser: false,
        opportunityFinder: false,
    };

    constructor(
        private authService: AuthenticationService,
        public router: Router,
        private readonly route: ActivatedRoute,
        private organizationService: OrganizationSetupFormService,
        private subscriptionState: SubscriptionStateService,
        private features: FeatureFlagService,
        private contractService: ContractService,
        private toast: ToastService,
        private documentTypeService: DocumentTypeService,
        private sanitizer: DomSanitizer,
    ) {
        super();
        this.currentUser = authService.getUser() as IUser;

        this.generateWithAISvg = this.sanitizer.bypassSecurityTrustHtml(EXPLORE_PAGE_SVG.GENERATE_WITH_AI);
        this.rfpGeneratorSvg = this.sanitizer.bypassSecurityTrustHtml(EXPLORE_PAGE_SVG.RFP_GENERATOR);
        this.contractGeneratorSvg = this.sanitizer.bypassSecurityTrustHtml(EXPLORE_PAGE_SVG.CONTRACT_GENERATOR);
        this.chatWithBobSvg = this.sanitizer.bypassSecurityTrustHtml(EXPLORE_PAGE_SVG.CHAT_WITH_BOB);
        this.proposalGeneratorSvg = this.sanitizer.bypassSecurityTrustHtml(EXPLORE_PAGE_SVG.PROPOSAL_GENERATOR);
        this.geotechnicalAnalysisSvg = this.sanitizer.bypassSecurityTrustHtml(EXPLORE_PAGE_SVG.GEOTECHNICAL_ANALSYIS);
    }

    ngOnInit() {
        this.checkForPaymentSuccess();
        this.subscribe(this.route.queryParams, params => {
            const openAction = params['openAction'];
            if (!openAction) return;
            this.router.navigate([], { relativeTo: this.route, queryParams: {}, replaceUrl: true });
            if (openAction === 'generate-with-ai') this.openAIAssistant();
            if (openAction === 'contract-generator') this.openGenerateContract();
        });
        this.loadSubscriptionDetails();
        this.getDocTypes();
    }

    /**
     * Load subscription details from cached state — no API call required.
     */
    private loadSubscriptionDetails() {
        const details = this.subscriptionState.subscriptionDetails();
        if (details) {
            this.subscriptionDetails = details;
        }
    }

    /**
     * @deprecated Use loadSubscriptionDetails() — now uses cached data only.
     */
    checkSubscriptionStatus() {
        this.loadSubscriptionDetails();
    }

    readonly generalAgentAllowed = computed(() => this.features.checkFeature(UI_FEATURE.GENERAL_AGENT).allowed);
    readonly documentsAllowed = computed(() => this.features.checkFeature(UI_FEATURE.DOCUMENTS).allowed);
    readonly geoTechnicalAnalysisAllowed = computed(() => this.features.checkFeature(UI_FEATURE.GEOTECHNICAL_ANALYSIS).allowed);

    get canSeeSettings() {
        return [SHARE_ACCESS.OWNER, SHARE_ACCESS.ADMIN].includes(this.currentUser.workspaceRole)
            || [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser.role);
    }

    openAskBob() {
        if (!this.generalAgentAllowed()) return;
        this.router.navigate(['/bob']);
    }

    navigateToChecklists() {
        this.router.navigate(['/settings/checklists-and-risks']);
    }

    navigateToSettings() {
        this.router.navigate(['/settings']);
    }

    openIntegrationsDialog() {
        this.router.navigate(['/settings/integrations']);
    }

    openInnDocAgent() {
        const token = localStorage.getItem('token');
        window.open(environment.innDocAgentUrl + '?token=' + token, '_blank');
    }

    /**
     * Navigate to the documents page in "create" mode.
     */
    newDocument(_event: any) {
        this.router.navigate(['/documents'], { queryParams: { create: 'true' } });
    }

    /**
     * Open the AI assistant dialog (Generate with AI flow). Guards against a
     * concurrent generation job already running.
     */
    openAIAssistant() {
        if (!this.documentsAllowed()) return;

        if (this.subscriptionState.hasSubscriptionEnded()) {
            this.subscriptionState.triggerSubscriptionDialog();
            return;
        }

        this.contractService.checkGenWithAiInProgress(DOCUMENT_CREATION_SOURCE.STANDARD).subscribe({
            next: res => {
                if (res && res.data) {
                    this.toast.warn('A document generation job is already in progress. Please wait for it to complete.');
                    return;
                }
                this.aiAssistant.open = true;
            },
            error: () => {
                this.aiAssistant.open = true;
            }
        });
    }

    /**
     * Open the Generate Contract dialog. Same guard as openAIAssistant().
     */
    openGenerateContract() {
        if (!this.documentsAllowed()) return;

        if (this.subscriptionState.hasSubscriptionEnded()) {
            this.subscriptionState.triggerSubscriptionDialog();
            return;
        }
        
        this.contractService.checkGenWithAiInProgress(DOCUMENT_CREATION_SOURCE.CONTRACT_GENERATION).subscribe({
            next: res => {
                if (res && res.data) {
                    this.toast.info('A document generation job is already in progress. Please wait for it to complete.');
                    return;
                }
                this.generateContract.open = true;
            },
            error: () => {
                this.generateContract.open = true;
            }
        });
    }

    onGenerateWithAi({ docTypeId: _docTypeId }: { docTypeId: string }) {
        this.aiAssistant.open = false;
    }

    onGenerateDocument({ docTypeId }: { docTypeId: string }) {
        this.generateContract.open = false;
        this.router.navigate(['/generate-with-ai'], {
            queryParams: docTypeId ? { docTypeId } : {}
        });
    }

    getDocTypeId(docType: string): string {
        const matched = this.docTypes.find((dt: IDocumentType) => dt.name === docType);
        return matched?._id ?? '';
    }

    getDocTypes() {
        this.documentTypeService.getDocTypes([DocumentTypeFilter.GENERAL, DocumentTypeFilter.FORM_RELATED]).subscribe({
            next: (res) => {
                const data = (res as { data?: IDocumentType[] })?.data;
                this.docTypes = data ?? [];
            },
            error: () => []
        });
    }

    /**
     * Navigate to the Generate with AI page, optionally pre-filtering by
     * document type.
     */
    navigateToGenerateWithAI(docType: string) {
        const matched = this.docTypes.find((dt: IDocumentType) => dt.name === docType);
        const docTypeId = matched?._id ?? null;
        this.router.navigate(['/generate-with-ai'], {
            queryParams: docTypeId ? { docTypeId } : {}
        });
    }

    navigateToGeotechnicalAnalysis() {
        if (!this.geoTechnicalAnalysisAllowed()) return;
        this.router.navigate(['/geotechnical-analysis']);
    }

    /**
     * Returns whether a feature card should render in its active (clickable)
     * state vs. the "Coming soon" overlay.
     */
    isCardActive(cardId: string): boolean {
        return !!this.cardStatus[cardId];
    }

    /**
     * Loads the welcome animation JSON used by the lottie player.
     */
    loadWelcomeAnimation() {
        fetch('/animations/welcome.json')
            .then(response => {
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                return response.json();
            })
            .then(data => { this.welcomeAnimationData = data; })
            .catch(error => {
                console.error('Error loading welcome animation:', error);
            });
    }

    hideWelcomeAnimation() {
        this.showWelcomeAnimation = false;
    }

    /**
     * Loads the celebration animation JSON used by the lottie player.
     */
    loadCelebrationAnimation() {
        fetch('/animations/celebration.json')
            .then(response => {
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                return response.json();
            })
            .then(data => { this.celebrationAnimationData = data; })
            .catch(error => {
                console.error('Error loading celebration animation:', error);
            });
    }

    showCelebration() {
        this.showCelebrationAnimation = true;
        this.loadCelebrationAnimation();
        setTimeout(() => this.hideCelebration(), 8000);
    }

    hideCelebration() {
        this.showCelebrationAnimation = false;
    }

    showWelcome() {
        this.showWelcomeAnimation = true;
        this.loadWelcomeAnimation();
        setTimeout(() => this.hideWelcomeAnimation(), 5000);
    }

    /**
     * Detect a Stripe payment-return on the URL and:
     *   1. Refresh subscription state from backend
     *   2. Play the welcome + celebration animations
     *   3. Clear the payment-success flag and URL params
     */
    checkForPaymentSuccess() {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const paymentSuccessFlag = sessionStorage.getItem('payment_success');

        if (sessionId && paymentSuccessFlag) {
            this.isProcessingPayment = true;

            this.subscriptionState.refreshSubscriptionDetails().subscribe({
                next: () => {
                    console.log('Subscription refreshed after payment success');
                    this.isProcessingPayment = false;
                    this.showWelcomeAnimation = true;
                    this.showWelcome();
                    this.showCelebration();
                    window.history.replaceState({}, '', window.location.pathname);
                    sessionStorage.removeItem('payment_success');
                },
                error: (error) => {
                    console.error('Error refreshing subscription after payment:', error);
                    this.isProcessingPayment = false;
                    this.showWelcomeAnimation = true;
                    this.showWelcome();
                    this.showCelebration();
                    window.history.replaceState({}, '', window.location.pathname);
                    sessionStorage.removeItem('payment_success');
                }
            });
        } else {
            this.isProcessingPayment = false;
        }
    }

    openComingSoon() {
        window.open('https://inncircles.ai', '_blank');
    }

    /**
     * Reset the rename dialog state.
     */
    resetRename() {
        this.rename = { open: false, newName: '', contractId: '' };
    }

    /**
     * Submit the rename dialog. Wired for future re-use by workspace-overview
     * widgets.
     */
    renameContract(_event: Event) {
        if (!this.rename.newName.trim()) this.rename.newName = 'Untitled';
        this.contractService.updateContract(this.rename.contractId, { name: this.rename.newName }).subscribe(res => {
            if (res.success) {
                this.toast.success('Document renamed successfully');
                this.contractService.contractsUpdated.next();
            } else {
                this.toast.error('Error renaming document');
            }
            this.resetRename();
        });
    }

    /**
     * Reset the due-date dialog state.
     */
    resetDueDate() {
        this.dueDate = { open: false, selectedDate: null, contractId: '' };
    }

    /**
     * Save the due-date dialog. Wired for future re-use by workspace-overview
     * widgets.
     */
    saveDueDate(event: Event) {
        event.stopPropagation();
        const dueDateValue = this.dueDate.selectedDate || undefined;
        this.contractService.updateContract(this.dueDate.contractId, { dueDate: dueDateValue }).subscribe(res => {
            if (res.success) {
                this.toast.success('Due date updated successfully');
                this.contractService.contractsUpdated.next();
            } else {
                this.toast.error('Error updating due date');
            }
            this.resetDueDate();
        });
    }

    /**
     * Confirm the deletion of the document held in `contractToDelete`. Wired
     * for future re-use by workspace-overview widgets.
     */
    confirmDeleteContract() {
        if (!this.contractToDelete) return;
        this.contractService.deleteContract(this.contractToDelete.id).subscribe(res => {
            if (res.success) {
                this.toast.success('Document deleted successfully');
                this.contractService.contractsUpdated.next();
                this.deleteDialogVisible = false;
                this.contractToDelete = null;
            } else {
                this.toast.error('Error deleting document');
                this.deleteDialogVisible = false;
                this.contractToDelete = null;
            }
        });
    }
}
